import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Trophy, Users, Star, Menu, X, Home, Database, Building2, Zap } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO, { PAGE_SEO } from '@/components/SEO';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Primary niches for the leaderboard
const PRIMARY_NICHES = [
  'Technology',
  'Gaming', 
  'Finance',
  'Fitness',
  'Education',
  'Entertainment',
  'Lifestyle',
  'Business'
];

export const Leaderboards = () => {
  const [creators, setCreators] = useState([]);
  const [allCreators, setAllCreators] = useState([]); // Store all fetched creators
  const [loading, setLoading] = useState(true);
  const [activeNiche, setActiveNiche] = useState('Technology');
  const [showingFallback, setShowingFallback] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch creators for a specific niche
  const fetchCreatorsForNiche = useCallback(async (niche) => {
    setLoading(true);
    setShowingFallback(false);
    
    try {
      const response = await axios.get(`${API}/youtube/search-creators?niche=${encodeURIComponent(niche)}&limit=20`);
      
      let data = response.data.creators || [];
      
      // Filter to quality creators (>= 1000 subscribers)
      data = data.filter(c => c.subscriber_count && c.subscriber_count >= 1000);
      
      // Sort by subscribers (descending)
      data.sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
      
      // If no creators found for this niche, show fallback
      if (data.length === 0 && allCreators.length > 0) {
        setCreators(allCreators.slice(0, 10));
        setShowingFallback(true);
      } else if (data.length === 0) {
        // Fetch general popular creators as fallback
        await fetchFallbackCreators();
      } else {
        setCreators(data.slice(0, 10)); // Top 10
      }
      
    } catch (err) {
      console.error('Failed to load creators for niche:', err);
      
      // On error, show fallback if available
      if (allCreators.length > 0) {
        setCreators(allCreators.slice(0, 10));
        setShowingFallback(true);
      } else {
        await fetchFallbackCreators();
      }
    } finally {
      setLoading(false);
    }
  }, [allCreators]);

  // Fetch fallback creators (popular across all categories)
  const fetchFallbackCreators = async () => {
    try {
      const response = await axios.get(`${API}/youtube/search-creators?niche=popular youtube creators&limit=20`);
      let data = response.data.creators || [];
      
      data = data.filter(c => c.subscriber_count && c.subscriber_count >= 1000);
      data.sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
      
      setAllCreators(data);
      setCreators(data.slice(0, 10));
      setShowingFallback(true);
    } catch (err) {
      console.error('Failed to load fallback creators:', err);
      setCreators([]);
    }
  };

  // Initial load - fetch for first niche and also get general creators
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      // First, fetch popular creators to have as fallback
      try {
        const fallbackResponse = await axios.get(`${API}/youtube/search-creators?niche=popular youtube creators&limit=30`);
        let fallbackData = fallbackResponse.data.creators || [];
        fallbackData = fallbackData.filter(c => c.subscriber_count && c.subscriber_count >= 1000);
        fallbackData.sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
        setAllCreators(fallbackData);
      } catch (err) {
        console.error('Failed to load fallback creators:', err);
      }
      
      // Then fetch for active niche
      await fetchCreatorsForNiche(activeNiche);
    };
    
    init();
  }, []);

  // Handle niche change
  const handleNicheChange = (niche) => {
    setActiveNiche(niche);
    fetchCreatorsForNiche(niche);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Meta Tags */}
      <SEO {...PAGE_SEO.leaderboards} />
      
      {/* Navigation - Responsive */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo-link">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <span className="text-lg md:text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">
                <Database className="w-4 h-4 mr-1.5" />
                Discover Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">
                <Users className="w-4 h-4 mr-1.5" />
                Discover Creators
              </Button>
            </Link>
            <Link to="/leaderboards">
              <Button variant="ghost" size="sm" className="text-indigo-600 bg-indigo-50">
                <Trophy className="w-4 h-4 mr-1.5" />
                Leaderboards
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
          
          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="h-8 px-2">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-slate-700" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              <Link 
                to="/" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Home</span>
              </Link>
              <Link 
                to="/explore-brands" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Database className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Brands</span>
              </Link>
              <Link 
                to="/brands/discover" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Creators</span>
              </Link>
              <Link 
                to="/leaderboards" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-indigo-50 text-indigo-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Leaderboards</span>
              </Link>
              <Link 
                to="/brands/login" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">For Brands</span>
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section - Responsive */}
        <div className="mb-8 sm:mb-12 text-center">
          {/* Hero heading container with max-width for better line breaks */}
          <div className="max-w-xl sm:max-w-2xl mx-auto mb-4 sm:mb-6">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
              <Trophy className="w-7 h-7 sm:w-10 sm:h-10 text-indigo-600 flex-shrink-0" />
              <h1 
                className="font-bold tracking-tight text-slate-900" 
                style={{
                  fontFamily: 'Manrope',
                  fontSize: 'clamp(1.5rem, 4vw + 0.5rem, 2.5rem)',
                  lineHeight: '1.15'
                }} 
                data-testid="leaderboards-heading"
              >
                Creator Leaderboards
              </h1>
            </div>
          </div>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed max-w-lg mx-auto px-4 sm:px-0">
            Top creators ranked by audience size
          </p>
        </div>

        {/* Niche Tabs - Responsive */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            <h2 className="text-base sm:text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Select Category</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {PRIMARY_NICHES.map((niche) => (
              <button
                key={niche}
                onClick={() => handleNicheChange(niche)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  activeNiche === niche 
                    ? 'bg-indigo-600 text-white shadow-sm' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-indigo-300'
                }`}
                data-testid={`niche-btn-${niche.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {niche}
              </button>
            ))}
          </div>
        </div>

        {/* Fallback Notice */}
        {showingFallback && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
            <Users className="w-5 h-5" />
            <span>Showing top creators while we gather more data for this category</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Loading creators...</p>
            </div>
          </div>
        )}

        {/* Leaderboard Results */}
        {!loading && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                {showingFallback ? 'Top Creators' : activeNiche}
              </h3>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 self-start sm:self-auto">
                {creators.length} creators
              </span>
            </div>

            <div className="space-y-4">
              {creators.length > 0 ? (
                creators.map((creator, index) => (
                  <Card key={creator.channel_id || creator.creator_id || index} className="p-4 sm:p-5" data-testid={`leaderboard-item-${index}`}>
                    {/* Mobile: Stacked layout, Desktop: Row layout */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                      
                      {/* Left Section: Rank + Avatar + Name */}
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        {/* Rank Badge */}
                        <div className="flex-shrink-0">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-lg sm:text-xl font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-slate-200 text-slate-700' :
                            index === 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-50 text-slate-600'
                          }`} style={{fontFamily: 'JetBrains Mono'}}>
                            #{index + 1}
                          </div>
                        </div>
                        
                        {/* Avatar */}
                        {creator.thumbnail && (
                          <img 
                            src={creator.thumbnail} 
                            alt={creator.channel_name}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                          />
                        )}
                        
                        {/* Name */}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate" style={{fontFamily: 'Manrope'}}>
                            {creator.channel_name || creator.title}
                          </h3>
                          {creator.description && (
                            <p className="text-xs sm:text-sm text-slate-500 truncate hidden sm:block">
                              {creator.description}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Right Section: Stats - Stacked on mobile, inline on desktop */}
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 pl-13 sm:pl-0">
                        {/* Subscribers - Single format only */}
                        <div className="flex justify-between sm:flex-col sm:text-center sm:px-4 sm:min-w-[100px]">
                          <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider">Subscribers</p>
                          <p className="text-base sm:text-xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                            {creator.subscriber_count >= 1000000 
                              ? `${(creator.subscriber_count / 1000000).toFixed(1)}M`
                              : creator.subscriber_count >= 1000
                                ? `${(creator.subscriber_count / 1000).toFixed(0)}K`
                                : creator.subscriber_count?.toLocaleString() || '0'}
                          </p>
                        </div>

                        {/* Brand Mentions (if available) */}
                        {(creator.brand_mentions || creator.sponsorship_count) && (
                          <div className="flex justify-between sm:flex-col sm:text-center sm:px-4 sm:min-w-[100px]">
                            <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider">Mentions</p>
                            <p className="text-base sm:text-xl font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>
                              {creator.brand_mentions || creator.sponsorship_count}
                            </p>
                          </div>
                        )}

                        {/* Video Count (if available) */}
                        {creator.video_count && (
                          <div className="flex justify-between sm:flex-col sm:text-center sm:px-4 sm:min-w-[100px]">
                            <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider">Videos</p>
                            <p className="text-base sm:text-xl font-bold text-slate-700" style={{fontFamily: 'JetBrains Mono'}}>
                              {creator.video_count >= 1000 
                                ? `${(creator.video_count / 1000).toFixed(1)}K`
                                : creator.video_count}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <Card className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Loading creators...</p>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* SEO Content Section - For Search Engines */}
      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* SEO-friendly H2 and description - visible on page load */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Top YouTube Creators by Niche
            </h2>
            <p className="text-slate-600 max-w-3xl leading-relaxed">
              Our creator leaderboards rank YouTube influencers by subscriber count across the most popular sponsorship categories. 
              Brands use these rankings to identify top-performing creators for sponsorship campaigns, while creators can benchmark their growth 
              against peers in their niche. Updated regularly with real YouTube data.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Categories */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Sponsorship Categories
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Technology & Software Reviews</li>
                <li>• Gaming & Esports Content</li>
                <li>• Finance & Investment Tips</li>
                <li>• Education & Online Learning</li>
                <li>• Fitness, Health & Lifestyle</li>
              </ul>
            </div>
            
            {/* Explore More */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Explore the Marketplace
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/brands/discover" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Find Creators for Brand Sponsorships
                  </Link>
                </li>
                <li>
                  <Link to="/explore-brands" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Browse Brands Sponsoring Creators
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Analyze Your YouTube Channel
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* For Brands */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Find YouTube Sponsors
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Looking for brands sponsoring creators in your niche? Sponsiq's influencer sponsorship marketplace connects top YouTube creators 
                with brands actively investing in creator partnerships. Use our leaderboards to find sponsorship opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer showDisclaimer={true} />
    </div>
  );
};

export default Leaderboards;
